import React from "react";
import { ASSETS } from "../lib/assets";
import { CTA, Eyebrow } from "../components/CTA";
import { useReveal } from "../lib/hooks";
import { Palmtree, Waves, Sun } from "lucide-react";

export default function Baru() {
  return (
    <main data-testid="baru-page" className="bg-[var(--pc-bg)]">
      <BaruHero />
    </main>
  );
}

/* ============================== HERO — dark relief map + left fade ============================== */
function BaruHero() {
  const head = useReveal();
  const body = useReveal();
  const stats = useReveal({ threshold: 0.28 });
  const bullets = useReveal({ threshold: 0.22 });
  const cta = useReveal();
  const img = useReveal({ threshold: 0.08 });

  return (
    <section
      data-testid="baru-hero"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Full-bleed dark relief map (per user spec) */}
      <div
        ref={img.ref}
        className={`absolute inset-0 reveal-img ${img.revealed ? "is-in" : ""}`}
      >
        <img
          src={ASSETS.baruRelieve}
          alt="Mapa de relieve de Barú"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        {/* Black fade on the left where text lives */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.96) 0%, rgba(7,7,7,0.88) 28%, rgba(7,7,7,0.55) 46%, rgba(7,7,7,0.18) 64%, rgba(7,7,7,0) 80%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.4) 0%, transparent 14%, transparent 80%, rgba(7,7,7,0.96) 100%)",
          }}
        />
      </div>

      <div
        className="relative z-10 pc-container flex flex-col justify-center"
        style={{ paddingTop: "190px", paddingBottom: "100px", minHeight: "100vh" }}
      >
        <div className="max-w-[620px]">
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <h1
              className="headline-xl"
              style={{
                fontSize: "clamp(31px, 3.7vw, 55px)",
                letterSpacing: "0.18em",
                lineHeight: 1.14,
                maxWidth: 580,
              }}
            >
              Asegura tu lugar antes de que se agote.
            </h1>
          </div>
          <p
            ref={body.ref}
            className={`body-soft mt-9 max-w-[480px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
            style={{ fontSize: 16.5 }}
          >
            Llegas en minutos desde Cartagena y, aun así, entras en otro tiempo.
            La mitad de la isla es patrimonio intocable y lo edificable se
            reduce cada año. Cuando construyes aquí, aseguras un lugar finito
            mientras todavía lo hay.
          </p>

          {/* Stats — 3 columns separated by hairlines */}
          <div
            ref={stats.ref}
            className="mt-14 grid grid-cols-3 max-w-[600px]"
          >
            <BigStat value="50%" label="Suelo protegido" show={stats.revealed} delayCls="delay-1" />
            <BigStat value="35" suffix="min" label="A Cartagena" show={stats.revealed} delayCls="delay-2" divider />
            <BigStat value="Limitada" label="Tierra edificable" show={stats.revealed} delayCls="delay-3" divider />
          </div>

          {/* 3 sensory bullets with icons */}
          <div
            ref={bullets.ref}
            className="mt-14 max-w-[560px] border-t border-[var(--pc-line)]"
          >
            <SensoryLine
              icon={<Palmtree size={22} strokeWidth={0.9} />}
              text="Siente la brisa que cruza la isla."
              show={bullets.revealed}
              delayCls="delay-1"
            />
            <SensoryLine
              icon={<Waves size={22} strokeWidth={0.9} />}
              text="Despierta frente a un mar que no se repite."
              show={bullets.revealed}
              delayCls="delay-2"
            />
            <SensoryLine
              icon={<Sun size={22} strokeWidth={0.9} />}
              text="Habita el silencio como privilegio."
              show={bullets.revealed}
              delayCls="delay-3"
            />
          </div>

          {/* CTA — same hairline style as everywhere */}
          <div
            ref={cta.ref}
            className={`mt-14 reveal delay-2 ${cta.revealed ? "is-in" : ""}`}
          >
            <CTA testId="baru-hero-cta" to="/contacto">
              Conversemos sobre Barú
            </CTA>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Sub-components -------------------- */
function BigStat({ value, suffix, label, show, delayCls = "", divider = false }) {
  return (
    <div
      className={`reveal ${delayCls} ${show ? "is-in" : ""} ${
        divider ? "border-l border-[var(--pc-line)] pl-3 sm:pl-6 lg:pl-7" : "pr-2 sm:pr-4"
      }`}
    >
      <div className="flex items-baseline gap-1.5">
        <span
          className="text-[var(--pc-text)]"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 200,
            fontSize: "clamp(20px, 5.4vw, 52px)",
            lineHeight: 1,
            letterSpacing: "0.02em",
          }}
        >
          {value}
        </span>
        {suffix && (
          <span
            className="text-[var(--pc-text-soft)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 300,
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
      <div
        className="mt-4 text-[var(--pc-bronze)]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 400,
          fontSize: 10.5,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </div>
  );
}

function SensoryLine({ icon, text, show, delayCls = "" }) {
  return (
    <div
      className={`flex items-center gap-7 py-5 border-b border-[var(--pc-line)] reveal ${delayCls} ${
        show ? "is-in" : ""
      }`}
    >
      <span className="text-[var(--pc-bronze)] shrink-0 opacity-90">{icon}</span>
      <p
        className="text-[var(--pc-text)]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 300,
          fontSize: 14,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          lineHeight: 1.4,
        }}
      >
        {text}
      </p>
    </div>
  );
}
